/**
 * DeckPickerModal - modal for selecting a deck and zone when bulk-adding cards.
 * Shows non-archived decks (active first, then brewing), sorted by name.
 * Locked decks are visible but disabled.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { IoLockClosed } from 'react-icons/io5';

import Modal from '@/components/Modal/Modal';
import { useDecksQuery } from '@/features/decks/hooks/api';
import { DECK_FORMAT_LABELS } from '@/lib/deckUtils';
import { getSelectableZones } from '@/lib/deckValidation';
import type { Deck, DeckZone } from '@/types/deck';

export interface DeckPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (deckId: string, zone: DeckZone) => void;
  isPending: boolean;
  selectedCount: number;
}

const ZONE_LABELS: Record<DeckZone, string> = {
  commander: 'Commander',
  companion: 'Companion',
  main: 'Main Deck',
  sideboard: 'Sideboard',
  maybeboard: 'Maybeboard',
  tokens: 'Tokens',
};

export default function DeckPickerModal({ isOpen, onClose, onConfirm, isPending, selectedCount }: DeckPickerModalProps): ReactElement {
  const { data: decks } = useDecksQuery();
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const [zone, setZone] = useState<DeckZone>('main');

  // Filter out archived decks, sort: active first, then brewing, then by name
  const availableDecks = useMemo(() => {
    if (!decks) return [];
    return decks
      .filter((d) => d.status !== 'archived')
      .sort((a, b) => {
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        return a.name.localeCompare(b.name);
      });
  }, [decks]);

  const selectedDeck = availableDecks.find((d) => d.id === selectedDeckId) ?? null;

  // Filter zones based on the selected deck's format and status
  const availableZones = useMemo(() => getSelectableZones(selectedDeck?.format ?? null, selectedDeck?.status ?? 'brewing'), [selectedDeck]);

  // Reset zone to 'main' if the current selection becomes unavailable
  const effectiveZone = availableZones.includes(zone) ? zone : 'main';

  const canConfirm = selectedDeck !== null && !selectedDeck.is_locked && !isPending;

  const handleConfirm = () => {
    if (!selectedDeckId || !canConfirm) return;
    onConfirm(selectedDeckId, effectiveZone);
  };

  const handleClose = () => {
    if (isPending) return;
    setSelectedDeckId(null);
    setZone('main');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add to Deck" size="md">
      <div className="space-y-4">
        {/* Deck list */}
        <div>
          <label className="text-silver mb-1.5 block text-sm font-medium">Deck</label>
          <div className="border-storm bg-slate/50 max-h-64 overflow-y-auto rounded-lg border">
            {availableDecks.length === 0 ? (
              <p className="text-ash px-4 py-6 text-center text-sm">No decks yet. Create a deck first.</p>
            ) : (
              availableDecks.map((deck) => (
                <DeckRow key={deck.id} deck={deck} isSelected={selectedDeckId === deck.id} onSelect={setSelectedDeckId} />
              ))
            )}
          </div>
        </div>

        {/* Zone selector (shown after deck is picked) */}
        {selectedDeck && (
          <div>
            <label className="text-silver mb-1.5 block text-sm font-medium">Zone</label>
            <div className="flex gap-2">
              {availableZones.map((z) => (
                <button
                  key={z}
                  type="button"
                  onClick={() => setZone(z)}
                  className={clsx(
                    'rounded-lg px-3 py-1.5 text-sm transition-colors',
                    effectiveZone === z
                      ? 'bg-aether/20 text-pearl border-aether/40 border'
                      : 'bg-slate text-ash border-storm hover:text-silver border',
                  )}
                >
                  {ZONE_LABELS[z]}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="text-silver hover:text-pearl rounded-lg px-4 py-2 text-sm transition-colors"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-aether hover:bg-aether/80 text-pearl rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isPending ? `Adding ${selectedCount.toLocaleString()} cards...` : `Add ${selectedCount.toLocaleString()} cards`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// DeckRow
// ============================================================================

interface DeckRowProps {
  deck: Deck;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

function DeckRow({ deck, isSelected, onSelect }: DeckRowProps): ReactElement {
  const isDisabled = deck.is_locked;
  const statusDot = deck.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500';

  return (
    <button
      type="button"
      onClick={() => !isDisabled && onSelect(deck.id)}
      disabled={isDisabled}
      className={clsx(
        'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
        isDisabled && 'cursor-not-allowed opacity-50',
        isSelected && !isDisabled && 'bg-aether/15 text-pearl',
        !isSelected && !isDisabled && 'text-silver hover:bg-slate hover:text-pearl',
      )}
    >
      {/* Status dot */}
      <span className={clsx('h-2 w-2 shrink-0 rounded-full', statusDot)} />

      {/* Deck name */}
      <span className="min-w-0 flex-1 truncate">{deck.name}</span>

      {/* Format badge */}
      {deck.format && <span className="bg-slate text-ash shrink-0 rounded px-1.5 py-0.5 text-xs">{DECK_FORMAT_LABELS[deck.format]}</span>}

      {/* Card count */}
      <span className="text-ash shrink-0 text-xs">{deck.card_count}</span>

      {/* Lock indicator */}
      {deck.is_locked && <IoLockClosed className="text-ash h-3.5 w-3.5 shrink-0" title="Deck is locked" />}
    </button>
  );
}
